import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider
} from '@mui/material';
import {
  History as HistoryIcon,
  RestoreOutlined as RestoreIcon,
  CompareArrows as DiffIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { auditLogService, AuditLog } from '../../services/auditLogService';
// @ts-ignore - react-json-view doesn't have TypeScript types available
import ReactJson from 'react-json-view';

interface EntityHistoryProps {
  entityType: string;
  entityId: string;
  onRestoreVersion?: (version: AuditLog) => Promise<void>;
  canRestore?: boolean;
  withoutConfirmation?: boolean;
}

const EntityHistory: React.FC<EntityHistoryProps> = ({
  entityType,
  entityId,
  onRestoreVersion,
  canRestore = false,
  withoutConfirmation = false
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [selectedVersion, setSelectedVersion] = useState<AuditLog | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [restoring, setRestoring] = useState<boolean>(false);
  const [data, setData] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState({ pages: 1 });

  const limit = 10;

  useEffect(() => {
    loadEntityHistory();
  }, [entityType, entityId, page]);

  const loadEntityHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await auditLogService.getEntityHistory(entityType, entityId, page, limit);
      
      // Update data and pagination state
      setData(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading entity history:', error);
      setError('خطا در بارگذاری تاریخچه تغییرات');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleViewDetails = (item: AuditLog) => {
    setSelectedVersion(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleRestore = async () => {
    if (!selectedVersion || !onRestoreVersion) return;

    setRestoring(true);
    try {
      await onRestoreVersion(selectedVersion);
      setDialogOpen(false);
      // Reload history after restore
      await loadEntityHistory();
    } catch (err) {
      console.error('Error restoring version:', err);
      setError('خطا در بازگرداندن نسخه');
    } finally {
      setRestoring(false);
    }
  };

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (data.length === 0 && !loading) {
    return (
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        هیچ تاریخچه‌ای برای این موجودیت یافت نشد.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          تاریخچه تغییرات
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadEntityHistory}
        >
          بروزرسانی
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>عملیات</TableCell>
                <TableCell>کاربر</TableCell>
                <TableCell>زمان</TableCell>
                <TableCell>نوع تغییر</TableCell>
                <TableCell>فیلدهای تغییر یافته</TableCell>
                <TableCell>جزئیات</TableCell>
                {canRestore && <TableCell>بازگرداندن</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.length > 0 ? data.map((item) => (
                <TableRow key={item._id} sx={{ 
                  backgroundColor: 
                    item.action === 'create' ? 'rgba(76, 175, 80, 0.1)' : 
                    item.action === 'update' ? 'rgba(33, 150, 243, 0.1)' : 
                    item.action === 'delete' ? 'rgba(244, 67, 54, 0.1)' : 
                    'transparent' 
                }}>
                  <TableCell>
                    <Tooltip title="مشاهده جزئیات">
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewDetails(item)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{item.userId?.name || item.performedBy?.fullName || 'سیستم'}</TableCell>
                  <TableCell dir="ltr">
                    {auditLogService.formatDate(item.metadata?.timestamp || item.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={auditLogService.getActionDisplayName(item.action)} 
                      size="small" 
                      color={
                        item.action === 'create' ? 'success' : 
                        item.action === 'update' ? 'info' : 
                        item.action === 'delete' ? 'error' : 'default'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {item.changes && Array.isArray(item.changes) ? (
                      // Format for array of changes
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.changes.map((change, index) => (
                          <Chip 
                            key={index} 
                            label={auditLogService.getFieldDisplayName(change.field)} 
                            size="small" 
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    ) : item.changes && typeof item.changes === 'object' ? (
                      // Format for object of changes
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.keys(item.changes).map(field => (
                          <Chip 
                            key={field} 
                            label={auditLogService.getFieldDisplayName(field)} 
                            size="small" 
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      item.action === 'create' ? (
                        <Typography variant="caption">ایجاد رکورد جدید</Typography>
                      ) : item.action === 'delete' ? (
                        <Typography variant="caption">حذف رکورد</Typography>
                      ) : (
                        <Typography variant="caption">-</Typography>
                      )
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<DiffIcon />}
                      onClick={() => handleViewDetails(item)}
                    >
                      جزئیات
                    </Button>
                  </TableCell>
                  {canRestore && onRestoreVersion && (
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        color="warning"
                        startIcon={<RestoreIcon />}
                        onClick={() => {
                          if (withoutConfirmation) {
                            setRestoring(true);
                            onRestoreVersion(item)
                              .then(() => {
                                loadEntityHistory();
                              })
                              .catch((err) => {
                                console.error('Error restoring version:', err);
                                setError('خطا در بازگرداندن نسخه');
                              })
                              .finally(() => {
                                setRestoring(false);
                              });
                          } else {
                            setSelectedVersion(item);
                            setDialogOpen(true);
                          }
                        }}
                        disabled={restoring}
                      >
                        بازگرداندن
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={canRestore ? 7 : 6} align="center">
                    <Typography variant="body2">
                      هیچ تاریخچه‌ای یافت نشد.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            sx={{ '& .MuiPaginationItem-root': { fontFamily: 'inherit' } }}
          />
        </Box>
      )}

      {/* Dialog for viewing version details */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle dir="rtl">
          جزئیات تغییرات - {selectedVersion && auditLogService.formatDate(selectedVersion.metadata?.timestamp || selectedVersion.timestamp)}
        </DialogTitle>
        <DialogContent dividers>
          {selectedVersion && (
            <Box dir="rtl">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    نوع تغییر:
                    <Chip
                      label={auditLogService.getActionDisplayName(selectedVersion.action)}
                      size="small"
                      color={
                        selectedVersion.action === 'create' ? 'success' :
                          selectedVersion.action === 'update' ? 'info' :
                            selectedVersion.action === 'delete' ? 'error' : 'default'
                      }
                      sx={{ mr: 1 }}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    کاربر: {selectedVersion.userId?.name || selectedVersion.performedBy?.fullName || 'سیستم'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    زمان: {auditLogService.formatDate(selectedVersion.metadata?.timestamp || selectedVersion.timestamp)}
                  </Typography>
                </Grid>
                {selectedVersion.metadata && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        آدرس IP: {selectedVersion.metadata.ip || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        روش درخواست: {selectedVersion.metadata.method || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        مسیر: {selectedVersion.metadata.route || '-'}
                      </Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
              </Grid>
              
              {/* Changes section for 'update' action */}
              {selectedVersion && selectedVersion.action === 'update' && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                    تغییرات اعمال شده:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>فیلد</TableCell>
                            <TableCell>مقدار قبلی</TableCell>
                            <TableCell>مقدار جدید</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedVersion.changes && Array.isArray(selectedVersion.changes) ? (
                            // Array format of changes
                            selectedVersion.changes.map((change, index) => (
                              <TableRow key={index}>
                                <TableCell>{auditLogService.getFieldDisplayName(change.field)}</TableCell>
                                <TableCell>
                                  {change.oldValue === "[REDACTED]" ? 
                                    "مخفی شده" : 
                                    change.oldValue !== undefined ? 
                                      JSON.stringify(change.oldValue, null, 2) : 
                                      'خالی'
                                  }
                                </TableCell>
                                <TableCell>
                                  {change.newValue === "[REDACTED]" ? 
                                    "مخفی شده" : 
                                    change.newValue !== undefined ? 
                                      JSON.stringify(change.newValue, null, 2) : 
                                      'خالی'
                                  }
                                </TableCell>
                              </TableRow>
                            ))
                          ) : selectedVersion.changes && typeof selectedVersion.changes === 'object' ? (
                            // Object format of changes
                            Object.entries(selectedVersion.changes).map(([field, values]: [string, any]) => (
                              <TableRow key={field}>
                                <TableCell>{auditLogService.getFieldDisplayName(field)}</TableCell>
                                <TableCell>
                                  {values.oldValue !== undefined 
                                    ? JSON.stringify(values.oldValue, null, 2) 
                                    : 'خالی'}
                                </TableCell>
                                <TableCell>
                                  {values.newValue !== undefined 
                                    ? JSON.stringify(values.newValue, null, 2) 
                                    : 'خالی'}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                <Typography variant="body2">
                                  هیچ تغییری برای نمایش وجود ندارد.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </>
              )}
              
              {/* Show previous state and new state for all actions */}
              {selectedVersion && selectedVersion.previousState && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                    وضعیت قبلی:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <ReactJson 
                      src={selectedVersion.previousState} 
                      name={null} 
                      collapsed={1}
                      displayDataTypes={false}
                      enableClipboard={false}
                      theme="monokai"
                    />
                  </Paper>
                </>
              )}
              
              {selectedVersion && selectedVersion.newState && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                    وضعیت جدید:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <ReactJson 
                      src={selectedVersion.newState} 
                      name={null} 
                      collapsed={1}
                      displayDataTypes={false}
                      enableClipboard={false}
                      theme="monokai"
                    />
                  </Paper>
                </>
              )}
              
              {/* Legacy format support */}
              {selectedVersion && selectedVersion.action === 'create' && selectedVersion.document && !selectedVersion.newState && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                    رکورد ایجاد شده:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <ReactJson 
                      src={selectedVersion.document} 
                      name={null} 
                      collapsed={1}
                      displayDataTypes={false}
                      enableClipboard={false}
                      theme="monokai"
                    />
                  </Paper>
                </>
              )}
              
              {selectedVersion && selectedVersion.action === 'delete' && selectedVersion.document && !selectedVersion.previousState && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
                    رکورد حذف شده:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <ReactJson 
                      src={selectedVersion.document} 
                      name={null} 
                      collapsed={1}
                      displayDataTypes={false}
                      enableClipboard={false}
                      theme="monokai"
                    />
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {canRestore && onRestoreVersion && selectedVersion && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<RestoreIcon />}
              onClick={handleRestore}
              disabled={restoring}
            >
              {restoring ? 'در حال بازگرداندن...' : 'بازگرداندن به این نسخه'}
            </Button>
          )}
          <Button onClick={handleCloseDialog}>
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityHistory; 